import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ContextKeys, SkylabConfig } from '@skylab-shared/business';
import { Company } from '@skylab-shared/dataModel';
import { AppStates, ViewLanguage } from '@skylab-shared/scenes';

import { EventBus, EventBusBase, injectTranslation, RootContext, RootContextBase, RoutingService } from '@skylab-angular-shared/core';
import { InsertUserPanelComponent, KsefConfigurationStatusButtonComponent, ViewLanguageService } from '@skylab-angular-shared/scenes';
import { RouterLink } from '@angular/router';
import { CompanyContextLink, TranslatePipe } from '@skylab-angular-shared/pipes';
import { LanguagePickerKIComponent, TooltipDirective, injectToast } from '@skylab-angular-shared/ui';
import { AnnouncementsButtonComponent } from '../announcements/announcements-button';

type StateName = 'help' | 'forum' | 'configuration';

@Component({
    selector: 'skl-app-bar',
    templateUrl: './app-bar.component.html',
    styleUrls: ['./app-bar.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [KsefConfigurationStatusButtonComponent, RouterLink, TooltipDirective, InsertUserPanelComponent, LanguagePickerKIComponent, TranslatePipe, CompanyContextLink, AnnouncementsButtonComponent]
})
export class AppBarComponent implements OnInit {
    protected readonly _T = injectTranslation();
    readonly #rootContext = inject(RootContext) as unknown as RootContextBase;
    readonly #eventBus = inject(EventBus) as unknown as EventBusBase;
    readonly #viewLanguageService = inject(ViewLanguageService);
    readonly #routingService = inject(RoutingService);
    readonly #destroyRef = inject(DestroyRef);

    public readonly companyName = signal<string>('');
    public readonly language = signal<ViewLanguage | undefined>(undefined);

    public readonly toast = injectToast();

    public get dashboardUrl(): string {
        return AppStates.dashboard.url;
    }

    public async ngOnInit(): Promise<void> {
        this.language.set(this.#viewLanguageService.selectedLanguage);

        await this.#refreshCompanyInfoAsync();

        const companyInfoChangeObserverId = this.#eventBus.observe('company-refresh', () => {
            this.#refreshCompanyInfoAsync();
        });

        this.#destroyRef.onDestroy(() => {
            this.#eventBus.deleteObserver(companyInfoChangeObserverId);
        });
    }

    public get languages(): ViewLanguage[] {
        return this.#viewLanguageService.viewLanguages;
    }

    async #refreshCompanyInfoAsync(): Promise<void> {
        const company = await this.#rootContext.getAsync<Company>(ContextKeys.company);
        this.companyName.set(company.name);
    }

    public async changeLanguage(language: ViewLanguage): Promise<void> {
        if (this.language()?.value !== language.value) {
            this.language.set(language);
            this.#viewLanguageService.changeLanguage(language);
        }
    }

    public goTo(stateName: StateName): void {
        const states = {
            help: () => {
                const href = SkylabConfig.technicalSupportUrl;
                window.open(href, '_blank');
                (window as any).insertUserPanel?.closeAccountPanel();
            },
            forum: () => {
                const href = SkylabConfig.subiekt123ForumUrl;
                window.open(href, '_blank');
                (window as any).insertUserPanel?.closeAccountPanel();
            },
            configuration: () => {
                this.#routingService.navigate([AppStates.configurationMenu.url]);
                (window as any).insertUserPanel?.closeAccountPanel();
            }
        };

        states[stateName]?.();
    }
}
